import { useCallback, useMemo } from 'react';
import { EventPageVisibility, EventWebsiteGuestQuery } from '@graphql/generated';
import { useUserConsent } from '@shared/utils/userConsent/useUserConsent';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface UseAccommodationsVisibilityBannerArgs
  extends Readonly<{
    data: EventWebsiteGuestQuery | undefined;
    hasIdentifiedUserOnce: boolean;
    isAdmin: boolean;
    isPreviewing: boolean;
    isWebsitePublishCapabilityEnabled: boolean;
    isWebsitePublished: boolean;
  }> {}

export const useAccommodationsVisibilityBanner = (args: UseAccommodationsVisibilityBannerArgs) => {
  const { data, hasIdentifiedUserOnce, isAdmin, isPreviewing, isWebsitePublishCapabilityEnabled, isWebsitePublished } = args;

  const { getUserConsent, setUserConsent } = useUserConsent();
  const [isAccommodationsVisibilityBannerEnabled] = useIsFeatureEnabled('accommodationsVisibilityBanner');

  // Check if accommodations page exists and is hidden
  const accommodationsPage = data?.eventByName?.pages.find(page => page.pageSlug === 'accommodations');
  const isAccommodationsPageHidden = !accommodationsPage || accommodationsPage?.visibility === EventPageVisibility.hidden;

  // Check if user has already dismissed the banner
  const hasSeenAccommodationsVisibilityBanner = getUserConsent('hasSeenAccommodationsVisibilityBanner');

  // Determine if banner should be shown
  const showAccommodationsVisibilityBanner = useMemo(
    () =>
      !hasSeenAccommodationsVisibilityBanner &&
      isAccommodationsVisibilityBannerEnabled &&
      hasIdentifiedUserOnce &&
      isAdmin &&
      !isPreviewing &&
      ((isWebsitePublishCapabilityEnabled && isWebsitePublished) || !isWebsitePublishCapabilityEnabled) &&
      isAccommodationsPageHidden,
    [
      hasSeenAccommodationsVisibilityBanner,
      isAccommodationsVisibilityBannerEnabled,
      hasIdentifiedUserOnce,
      isAdmin,
      isPreviewing,
      isWebsitePublishCapabilityEnabled,
      isWebsitePublished,
      isAccommodationsPageHidden,
    ],
  );

  const handleCloseAccommodationsVisibilityBanner = useCallback(() => {
    setUserConsent('hasSeenAccommodationsVisibilityBanner', true);
  }, [setUserConsent]);

  return {
    showAccommodationsVisibilityBanner,
    handleCloseAccommodationsVisibilityBanner,
  };
};
